import { Box, Container } from '@mui/material';
import Loading, { LoadingBall } from '@ingka/loading';
import React, { useEffect, useState } from 'react';

import Button from '@ingka/button';
import { checkAccess } from '../utils/check-access.utils';
import { getContactPersons } from '../../../shared/utils/get-contact-persons.utils';
import { useAuth } from '../state/auth.state';
import styled from '@emotion/styled';
import { tokens } from '../../../shared/styles/core/tokens';

interface Contact {
  name: string;
  email: string;
  role?: string;
}

const DEFAULT_CONTACT: Contact = {
  name: 'System Administrator',
  email: 'admin@company.com',
  role: 'admin'
};

const StyledLoading = styled(Loading)`
  height: 500px;
`;

const StyledContainer = styled(Container)`
  text-align: center;
`;

const NoAccessMessage = styled.div`
  margin-top: ${tokens.space.lg};
  padding: ${tokens.space.lg};
`;

interface AccessControlProps {
  children: React.ReactNode;
}

export const AccessControl = ({ children }: AccessControlProps): JSX.Element => {
  const { hasRole } = useAuth();

  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessContact, setAccessContact] = useState<Contact>(DEFAULT_CONTACT);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const contacts = getContactPersons();
      const result = checkAccess(contacts);

      if (result.contact) {
        setAccessContact(result.contact);
      }

      setHasAccess(result.hasAccess);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Access check failed');
      setHasAccess(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  if (isChecking) {
    return (
      <Container maxWidth="xs">
        <Box sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <StyledLoading text="Checking access..." labelTransitions>
            <LoadingBall color="primary" />
          </StyledLoading>
        </Box>
      </Container>
    );
  }

  if (!hasAccess || !hasRole) {
    return (
      <StyledContainer maxWidth="sm">
        <NoAccessMessage>
          <h2>Access Required</h2>
          <p>
            {!hasRole
              ? 'Your account has not been assigned a role for this application.'
              : "You don't have access to this application."}
          </p>
          {error && (
            <p style={{ color: 'red', marginBottom: '1rem' }}>
              Error: {error}
            </p>
          )}
          <Button
            text="Request Access"
            onClick={() => {
              const subject = "MMA Access Request";
              const body = `Hello ${accessContact.name},\n\nI would like to request access to MMA.`;
              const mailtoUrl = `mailto:${accessContact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
              window.open(mailtoUrl, '_blank');
            }}
            type="primary"
            style={{ marginTop: tokens.space.lg }}
          />
        </NoAccessMessage>
      </StyledContainer>
    );
  }

  return <>{children}</>;
};